import { createRef, useEffect, useState, MouseEvent } from "react";
import { trns } from "../helper.js";
import TextInput from "./TextInput.js";
import NextButton from "./NextButton.js";
import withStateSubmit from "./withStateSubmit.js";
import { validPhone, validSMSCode } from "../validation.js";
import {
  StringMap,
  FifthStateSubmit,
  FifthState,
  Target,
  KeyboardEvent,
} from "../types/types.js";
import { Segment, Item, Heading, Button, Form } from "ui-components";
import useRegStore from "../store/index.js";
import { useShallow } from "zustand/react/shallow";
import { sendSMS, verifySMS } from "./../api.js";
type Props = {
  onSubmit: (e: KeyboardEvent | MouseEvent<HTMLButtonElement>) => Promise<void>;
  submitted: boolean;
  inFlight: boolean;
  isFinal: boolean;
  isStart: boolean;
  isReq: (req: string) => typeof req;
  handleField: (field: string, e?: Target) => void;
  dirty: StringMap<boolean>;
  toggleEdit: () => void;
  state: FifthState;
  changeState: (field: string, value?: string | number | boolean) => void;
  previousPage: () => void;
  servicePath: string;

  // Refs
  cell_phone_edit_ref: typeof createRef;
};

const state: FifthState = {
  cell_phone_edit: "",
  sms_code: "",
  phone_verification: "",
  skipped: false,
};

const Fifth = ({
  onSubmit,
  submitted,
  isFinal,
  isStart,
  isReq,
  handleField,
  dirty,
  state,
  changeState,
  previousPage,
  cell_phone_edit_ref,
  servicePath,
}: Props) => {
  const {
    phoneEdit,
    codeSent,
    tokenReceived,
    toggleEdit,
    cellPhone,
    setInflight,
    inFlight,
    setError,
    resetInflight,
    requestSmsResponse,
    verifySmsResponse,
  } = useRegStore(
    useShallow(state => ({
      phoneEdit: state.phone_edit,
      codeSent: state.code_sent,
      tokenReceived: state.token_received,
      toggleEdit: state.togglePhoneEdit,
      cellPhone: state.step2.cell_phone,
      setInflight: state.setInflight,
      inFlight: state.inFlight,
      setError: state.setError,
      resetInflight: state.resetInflight,
      requestSmsResponse: state.requestSmsResponse,
      verifySmsResponse: state.verifySmsResponse,
    }))
  );

  const requestSMS = async (phoneNumber: string) => {
    if (phoneNumber.length < 7) {
      setError(
        "danger",
        trns("IDS_LOGINBOX_REGISTRATION_INVALID_PHONE", "Not a valid phone number.")
      );
      resetInflight();
    }

    if (inFlight) {
      return;
    }

    try {
      setInflight();
      let resp = await sendSMS(phoneNumber, servicePath);
      requestSmsResponse(resp.sent);
    } catch (e) {
      setError(
        "danger",
        trns(
          "IDS_LOGINBOX_SMS_INVALID_PHONE_NUMBER",
          "We are unable to send the verification SMS code to this number, please edit and try again."
        )
      );
      resetInflight();
    }
  };

  const submitSMSCode = async (phoneNumber: string | number, code: string) => {
    if (code.length < 4) {
      setError(
        "danger",
        trns("IDS_LOGINBOX_SMS_INVALID_CODE", "Incorrect SMS verification code.")
      );
      resetInflight();
      return { token: null };
    }

    try {
      setInflight();
      const resp = await verifySMS(phoneNumber, code, servicePath);
      verifySmsResponse(resp.token && resp.token.length === 64 ? true : false);
      return resp;
    } catch (e) {
      setError(
        "danger",
        trns("IDS_LOGINBOX_SMS_INVALID_CODE", "Incorrect SMS verification code.")
      );
      resetInflight();
      return { token: null };
    }
  };

  useEffect(
    () => {
      const handleKeyDown = (e: KeyboardEvent | MouseEvent<HTMLButtonElement>) => {
        if (phoneEdit) {
          toggleEdit();
        } else if (!codeSent) {
          requestSMS(state.cell_phone_edit);
        } else if (state.phone_verification === "" && !tokenReceived) {
          submitSMSCode(state.cell_phone_edit, state.sms_code).then(r => {
            if (r.token) {
              handleField("phone_verification", { target: { value: r.token } });
            }
          });
        } else {
          onSubmit(e);
        }
      };

      if (cellPhone) changeState("cell_phone_edit", cellPhone);
      if (!cellPhone || cellPhone === "") toggleEdit();

      document.addEventListener("keydown", handleKeyDown, false);
      return document.removeEventListener("keydown", handleKeyDown, false);
    },
    [changeState, cellPhone]
  );

  const [disableResendBtn, setDisableResendBtn] = useState(false);
  const phoneRequired = isReq("phone_verification");

  return (
    <Form className="fieldset">
      <Segment className="sms-content">
        <Heading headingLevel="h4">
          {trns("IDS_SMS_VERIFICATION", "SMS Verification")}
          {!phoneRequired && " (" + trns("IDS_SMS_OPTIONAL", "Optional") + ")"}
        </Heading>

        {!state.skipped && (
          <p>
            {trns(
              "IDS_SMS_VERIFICATION_NOTICE",
              "Please take a moment to verify your phone number. This helps us confirm your identity and secure your account. We will send you a one-time verification code:"
            )}
          </p>
        )}

        <Item className="sms-content-block">
          {!(codeSent || state.skipped) &&
            (!phoneEdit ? (
              <Segment className="sms-mobile-num-send">
                <Segment className="grid-row">
                  <Item className="sms-mobile-num">{state.cell_phone_edit}</Item>

                  <Button
                    className="btn"
                    commonBtn
                    type="button"
                    disabled={false}
                    onClick={() => {
                      requestSMS(state.cell_phone_edit);
                    }}
                  >
                    {trns("IDS_SMS_SEND_CODE", "Send Verification Code")}
                  </Button>
                </Segment>

                <Segment className="sms-mobile-num-wrong ta_center">
                  <Item itemType="span" className="sms-label">
                    {trns("IDS_SMS_WRONG_NUMBER", "Wrong phone number?")}
                  </Item>

                  <Button className="common-link" type="button" onClick={toggleEdit}>
                    {trns("IDS_SMS_EDIT_NUMBER", "Edit Number")}
                  </Button>
                </Segment>
              </Segment>
            ) : (
              <Segment className="grid-row">
                <Segment className="sms-field">
                  <TextInput
                    placeholder={trns("IDS_MOBILE_PHONE", "Mobile Phone")}
                    label={trns("IDS_MOBILE_PHONE_PROMPT", "Mobile Phone:")}
                    type="tel"
                    onInput={v => {
                      let field = v.target.value.split("");
                      handleField("cell_phone_edit", {
                        target: {
                          value: field
                            .filter(
                              (c: string) => parseInt(c, 10) >= 0 && parseInt(c, 10) <= 9
                            )
                            .join(""),
                        },
                      });
                    }}
                    value={state.cell_phone_edit}
                    dirty={dirty.cell_phone_edit}
                    req
                    ref={cell_phone_edit_ref}
                    validator={validPhone}
                  />

                  {!tokenReceived && !state.skipped && (
                    <p className="helper-text">
                      {trns("IDS_SMS_COUNTRY_CODE", "Include your country and area code")}
                      <Item itemType="span">
                        {trns(
                          "IDS_SMS_COUNTRY_CODE_EXAMPLE_EU",
                          "e.g. International: 442071838750"
                        )}
                        <br />
                        {trns(
                          "IDS_SMS_COUNTRY_CODE_EXAMPLE_NA",
                          "e.g. North America: 1415555267"
                        )}
                      </Item>
                    </p>
                  )}
                </Segment>

                <Segment className="sms-btn-actions">
                  <Button
                    commonBtn
                    type="button"
                    onClick={toggleEdit}
                    disabled={!validPhone(state.cell_phone_edit).ok}
                  >
                    {trns("IDS_SMS_EDIT_NUMBER", "Confirm Number")}
                  </Button>
                </Segment>
              </Segment>
            ))}

          {codeSent && state.phone_verification === "" && !tokenReceived && (
            <Segment className="sms-verification">
              <Segment className="grid-row">
                <Segment className="sms-field">
                  <TextInput
                    placeholder={trns("IDS_MOBILE_PHONE_SMS", "SMS verification Code")}
                    label={trns("IDS_MOBILE_PHONE_PROMPT_SMS", "SMS verification Code:")}
                    onInput={v => handleField("sms_code", v)}
                    value={state.sms_code}
                    dirty={dirty.sms_code}
                    req
                    validator={validSMSCode}
                  />
                </Segment>

                <Segment className="sms-btn-actions">
                  <Button
                    commonBtn
                    type="button"
                    disabled={tokenReceived || inFlight}
                    onClick={() => {
                      submitSMSCode(state.cell_phone_edit, state.sms_code).then(r => {
                        if (r.token) {
                          handleField("phone_verification", { target: { value: r.token } });
                        }
                      });
                    }}
                  >
                    {trns("IDS_SMS_VERIFY_CODE", "Verify SMS Code")}
                  </Button>
                </Segment>
              </Segment>

              <Segment className="send-again">
                <Item itemType="span" className="sms-label">
                  {trns("IDS_SMS_CODE_MISSING", "Didn't receive code?")}
                </Item>

                <Button
                  type="button"
                  className="common-link"
                  disabled={disableResendBtn}
                  onClick={() => {
                    if (!disableResendBtn) {
                      requestSMS(state.cell_phone_edit);
                    }
                    setDisableResendBtn(true);
                    setTimeout(() => {
                      setDisableResendBtn(false);
                    }, 3000);
                  }}
                >
                  {trns("IDS_SMS_CODE_RESEND", "Resend Now")}
                </Button>
              </Segment>
            </Segment>
          )}

          {codeSent && tokenReceived && !state.skipped && (
            <Heading headingLevel="h4" centered>
              {trns("IDS_SMS_VERIFIED", "SMS verified")}
            </Heading>
          )}
        </Item>

        {!phoneRequired && !tokenReceived && !state.skipped && (
          <Segment className="sms-skip-step ta_center">
            <Button
              type="button"
              className="common-link"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                onSubmit(e);
                handleField("skipped", { target: { value: true } });
              }}
              disabled={false}
            >
              {trns("IDS_SMS_SKIP", "Skip this Step")}
            </Button>
          </Segment>
        )}
      </Segment>

      <Segment className="btn-toolbar reg-nav x">
        <Item className="grid grid-6">
          <Button
            className="btn"
            type="button"
            disabled={isStart}
            onClick={() => {
              !isStart && previousPage();
            }}
          />
        </Item>

        <Item className="grid grid-6">
          <NextButton
            disabled={submitted || inFlight || (!tokenReceived && !state.skipped)}
            onSubmit={onSubmit}
            isStart={isStart}
            isFinal={isFinal}
          />
        </Item>
      </Segment>
    </Form>
  );
};

export default withStateSubmit(Fifth, state, "step5", (t: FifthStateSubmit) => ({
  phone_verification_ref: (el: typeof createRef) => {
    t.phone_verification_ref = el;
  },
  cell_phone_edit_ref: (el: typeof createRef) => {
    t.cell_phone_edit_ref = el;
  },
}));
